import { Article, IosShare } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

import { COURT_CASES_V2_ROUTES } from '../../constants';
import { type ClassActionSearchCard } from '../../types';

import {
  ClassActionCardHeaderRow,
  ClassActionCardMainColumn,
  ClassActionCardRoot,
  ClassActionCardTopSection,
  ClassActionCaseTitleText,
  ClassActionCauseHeaderRow,
  ClassActionCauseLabel,
  ClassActionCausePanel,
  ClassActionCausePlaceholder,
  ClassActionCauseValue,
  ClassActionDefendantName,
  ClassActionMetaLine,
  ClassActionMetaStack,
  ClassActionShareIconButton,
} from './components';

import type { KeyboardEvent, MouseEvent } from 'react';

export interface ClassActionLawsuitResultCardProps {
  card: ClassActionSearchCard;
  isHovered?: boolean;
}

const ABSENT_CAUSE_PHRASES = new Set([
  'no cause code entered',
  'no cause listed',
  'not available',
  'n/a',
  'na',
  'none',
  '-',
  '—',
]);

const isAbsentCause = (raw: string | undefined): boolean => {
  const t = (raw ?? '').trim().toLowerCase();
  return t.length === 0 || ABSENT_CAUSE_PHRASES.has(t);
};

export const resolveClassActionDocketIdForReport = (
  card: ClassActionSearchCard
): number | null => {
  const id = card.docket_id;
  if (typeof id !== 'number' || !Number.isSafeInteger(id) || id <= 0) {
    return null;
  }
  return id;
};

export const ClassActionLawsuitResultCard = ({
  card,
  isHovered,
}: ClassActionLawsuitResultCardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const defendant = card.defendant?.trim() || '';
  const filedRaw = card.filed_on?.trim() || '';
  const filedLine = filedRaw ? `Filed: ${filedRaw}` : '';
  const court = card.court?.trim() || '';
  const courtLine = court ? `Court: ${court}` : '';
  const docketNumber = card.docket_number?.trim() || '';
  const docketLine = docketNumber ? `Docket Number: ${docketNumber}` : '';
  const cause = card.cause?.trim() || '';
  const hasCause = !isAbsentCause(card.cause);
  const reportDocketId = resolveClassActionDocketIdForReport(card);
  const canOpenReport = reportDocketId !== null;

  const metaLines = [
    courtLine ? { key: 'court', text: courtLine } : null,
    docketLine ? { key: 'docket', text: docketLine } : null,
    filedLine ? { key: 'filed', text: filedLine } : null,
  ].filter(Boolean) as Array<{ key: string; text: string }>;

  const returnTo = encodeURIComponent(
    location.pathname + window.location.search
  );
  const reportPath =
    reportDocketId === null
      ? null
      : `${COURT_CASES_V2_ROUTES.CLASS_ACTION_LAWSUIT_REPORT}/${reportDocketId}?returnTo=${returnTo}`;

  const openReport = () => {
    if (reportPath === null) {
      return;
    }
    navigate(reportPath);
  };

  const openReportInNewTab = () => {
    if (reportPath === null) {
      return;
    }
    window.open(reportPath, '_blank', 'noopener,noreferrer');
  };

  const handleShareClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    openReportInNewTab();
  };

  const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.currentTarget !== e.target) {
      return;
    }
    if (!canOpenReport) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openReport();
    }
  };

  return (
    <ClassActionCardRoot
      isHovered={isHovered}
      onClick={canOpenReport ? openReport : undefined}
      onKeyDown={canOpenReport ? handleCardKeyDown : undefined}
      role={canOpenReport ? 'button' : undefined}
      tabIndex={canOpenReport ? 0 : undefined}
      aria-label={
        canOpenReport
          ? `Open class action report for ${card.case_title}`
          : undefined
      }
      sx={canOpenReport ? { cursor: 'pointer' } : undefined}
    >
      <ClassActionCardTopSection>
        <ClassActionCardHeaderRow>
          <ClassActionCardMainColumn>
            <ClassActionDefendantName title={defendant || undefined}>
              {defendant || '—'}
            </ClassActionDefendantName>
            <ClassActionCaseTitleText>
              {card.case_title}
            </ClassActionCaseTitleText>
            {metaLines.length > 0 ? (
              <ClassActionMetaStack>
                {metaLines.map(({ key, text }) => (
                  <ClassActionMetaLine key={key}>{text}</ClassActionMetaLine>
                ))}
              </ClassActionMetaStack>
            ) : null}
          </ClassActionCardMainColumn>
          <ClassActionShareIconButton
            size="small"
            aria-label="Open class action report in new tab"
            onClick={handleShareClick}
            disabled={!canOpenReport}
          >
            <IosShare sx={{ fontSize: 20 }} />
          </ClassActionShareIconButton>
        </ClassActionCardHeaderRow>
      </ClassActionCardTopSection>

      <ClassActionCausePanel>
        <ClassActionCauseHeaderRow>
          <Article
            sx={(theme) => ({
              fontSize: 18,
              color: theme.palette.primary.main,
            })}
            aria-hidden
          />
          <ClassActionCauseLabel>Cause</ClassActionCauseLabel>
        </ClassActionCauseHeaderRow>
        {hasCause ? (
          <ClassActionCauseValue>{cause}</ClassActionCauseValue>
        ) : (
          <ClassActionCausePlaceholder>
            No cause listed
          </ClassActionCausePlaceholder>
        )}
      </ClassActionCausePanel>
    </ClassActionCardRoot>
  );
};
